import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import { submitButtonStyles } from "./styles";
type CrashCourseFormPayload = {
  topic: string;
  depth: "summary" | "detailed" | "comprehensive";
  format: "text" | "audio" | "text+audio";
};

interface CrashCourseFormProps {
  messageId: string;
  onFormSubmit?: (form: CrashCourseFormPayload) => void;
}

export const CrashCourseForm: React.FC<CrashCourseFormProps> = ({
  messageId,
  onFormSubmit,
}) => {
  const theme = useTheme();
  const [form, setForm] = useState<CrashCourseFormPayload>({
    topic: "",
    depth: "summary",
    format: "text",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | null>(
    null
  );

  const handleInputChange = (
    field: "topic" | "depth" | "format",
    value: any
  ) => {
    setForm((prev: CrashCourseFormPayload) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  useEffect(() => {
    if (submitStatus === "success") {
      toast.success(
        "Crash course request submitted! Generating your personalized crash course...",
        {
          position: "top-center",
          autoClose: 3000,
          closeOnClick: false,
          pauseOnHover: true,
        }
      );
    }
  }, [submitStatus]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    if (!form.topic.trim()) {
      newErrors.topic = "Topic is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (onFormSubmit) {
        onFormSubmit(form);
      }
      setSubmitStatus("success");
    } catch (error) {
      setSubmitStatus("error");
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        maxWidth: 600,
        mx: "auto",
        opacity: isSubmitting ? 0.6 : 1,
        transition: "opacity 0.3s ease",
      }}
    >
      <Typography variant="h6" gutterBottom color="primary">
        Create Crash Course
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define your topic and depth level for a personalized structured Crash
        Course.
      </Typography>

      {submitStatus === "error" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to create crash course. Please try again.
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Topic"
          value={form.topic}
          onChange={(e) => handleInputChange("topic", e.target.value)}
          placeholder="e.g., NATALEE vs. Monarch-E"
          error={!!errors.topic}
          helperText={errors.topic || "What do you want to learn about?"}
          required
          disabled={isSubmitting}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 2 }} disabled={isSubmitting}>
          <InputLabel>Depth Level</InputLabel>
          <Select
            value={form.depth}
            onChange={(e) => handleInputChange("depth", e.target.value)}
            label="Depth Level"
            disabled={isSubmitting}
          >
            <MenuItem value="summary">Summary (3-5 min overview)</MenuItem>
            <MenuItem value="detailed">Detailed (10-15 min deep dive)</MenuItem>
          </Select>
        </FormControl>

        <FormControl
          component="fieldset"
          sx={{ mb: 3 }}
          disabled={isSubmitting}
        >
          <FormLabel component="legend">Delivery Format</FormLabel>
          <RadioGroup
            value={form.format}
            onChange={(e) => handleInputChange("format", e.target.value)}
          >
            <FormControlLabel
              value="text"
              control={<Radio disabled={isSubmitting} />}
              label="Text only (quick scan)"
              sx={{
                color: theme.palette.text.primary,
              }}
            />
            <FormControlLabel
              value="audio"
              control={<Radio disabled={isSubmitting} />}
              label="Audio only (hands-free)"
              sx={{
                color: theme.palette.text.primary,
              }}
            />
            <FormControlLabel
              value="text+audio"
              control={<Radio disabled={isSubmitting} />}
              label="Text + Audio (comprehensive)"
              sx={{
                color: theme.palette.text.primary,
              }}
            />
          </RadioGroup>
        </FormControl>

        {submitStatus !== "success" && (
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              sx={submitButtonStyles}
            >
              {isSubmitting ? "Creating..." : "Create Crash Course"}
            </Button>
          </Box>
        )}
      </form>
    </Box>
  );
};
